import {
  TrackingContext,
  TrackingEventNames
} from 'common/constants/trackingEventsNames';
import eventEmitter from 'common/services/events/eventEmitter';

import { UserActionPayload } from 'website/reducers/user';

import * as DotNet2GraphApi from './DotNet2GraphApi';

/**
 * This file should contain / export all calls to secure-api.allocine.fr
 */

/**
 * Ask the api to load the favorite theaters of a user.
 *
 * The in-flight promise is cached so the several call sites that request the
 * favorite theaters on the same page (movie page bootstrap, favorites showtimes
 * tab, favorite-theater buttons) share a single fetch. This query paginates
 * through every favorite theater, so calling it several times is expensive for
 * users with many favorites. The cache is reset on failure (to allow a retry)
 * and whenever the favorites change (see add/removeFavoriteTheater).
 */
let userTheatersPromise: ReturnType<
  typeof DotNet2GraphApi.retrieveUserTheaters
> | null = null;

export const resetUserTheatersCache = () => {
  userTheatersPromise = null;
};

export const retrieveUserTheaters = () => {
  if (!userTheatersPromise) {
    userTheatersPromise = DotNet2GraphApi.retrieveUserTheaters().catch(
      error => {
        userTheatersPromise = null;
        throw error;
      }
    );
  }
  return userTheatersPromise;
};

/**
 * Ask the API to create a new favorite theater for the current user
 */
export const addFavoriteTheater = (
  theaterId: string,
  context: TrackingContext
) =>
  DotNet2GraphApi.addFavoriteTheater(theaterId).then(response => {
    resetUserTheatersCache();
    eventEmitter.emit(TrackingEventNames.ADD_USER_THEATER_EVENT, {
      context,
      response
    });
    return response;
  });

/**
 * Ask the api to remove a theater from favorites
 */
export const removeFavoriteTheater = (
  payload: UserActionPayload | null | undefined,
  context: TrackingContext
) =>
  DotNet2GraphApi.removeFavoriteTheater(payload)
    .then(response => {
      resetUserTheatersCache();
      eventEmitter.emit(TrackingEventNames.REMOVE_USER_THEATER_EVENT, {
        context,
        response
      });
      return response;
    })
    .then(() => payload);
